// Avatar system configuration and types

export type AvatarType = 'generated' | 'custom' | 'uploaded';

export interface AvatarConfig {
  category: 'human-male' | 'human-female' | 'animal' | 'object' | 'fantasy';
  style: string;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
  };
  features: {
    hair?: string;
    eyes?: string;
    clothing?: string;
    accessories?: string[];
  };
  animations: {
    idle: string;
    celebration: string;
    thinking: string;
  };
  seed?: string; // For deterministic generation
}

export const AVATAR_CATEGORIES = {
  'human-male': {
    name: '🧑 Dude',
    description: 'Cool guy avatars',
    styles: ['casual', 'sporty', 'formal', 'gamer', 'artist']
  },
  'human-female': {
    name: '👩 Girl',
    description: 'Awesome girl avatars', 
    styles: ['casual', 'sporty', 'formal', 'gamer', 'artist']
  },
  'animal': {
    name: '🐱 Animal',
    description: 'Cute animal friends',
    styles: ['cat', 'dog', 'fox', 'panda', 'wolf', 'tiger', 'rabbit', 'owl']
  },
  'object': {
    name: '🤖 Object',
    description: 'Cool objects & robots',
    styles: ['robot', 'alien', 'gem', 'crown', 'shield', 'lightning', 'star', 'crystal']
  },
  'fantasy': {
    name: '🧙 Fantasy',
    description: 'Magical creatures',
    styles: ['wizard', 'knight', 'dragon', 'unicorn', 'phoenix', 'mage', 'archer', 'warrior']
  }
} as const;

export const AVATAR_COLORS = {
  teen: {
    green: 'hsl(150, 80%, 50%)',
    blue: 'hsl(220, 80%, 60%)',
    purple: 'hsl(280, 70%, 65%)',
    gold: 'hsl(45, 90%, 60%)',
    pink: 'hsl(320, 70%, 70%)',
    orange: 'hsl(25, 95%, 60%)',
    red: 'hsl(0, 80%, 60%)',
    cyan: 'hsl(190, 80%, 50%)'
  },
  neutral: {
    light: 'hsl(0, 0%, 95%)',
    dark: 'hsl(210, 11%, 15%)',
    gray: 'hsl(210, 11%, 50%)',
    white: 'hsl(0, 0%, 100%)',
    black: 'hsl(0, 0%, 0%)'
  }
} as const;

export const AVATAR_ANIMATIONS = {
  idle: ['wave', 'nod', 'blink', 'smile', 'float'],
  celebration: ['dance', 'jump', 'cheer', 'victory', 'sparkle'],
  thinking: ['scratch-head', 'chin-rub', 'look-up', 'ponder', 'calculate']
} as const;

export const DEFAULT_AVATAR_CONFIG: AvatarConfig = {
  category: 'human-male',
  style: 'casual',
  colors: {
    primary: AVATAR_COLORS.teen.blue,
    secondary: AVATAR_COLORS.teen.green, 
    accent: AVATAR_COLORS.teen.gold
  },
  features: {
    hair: 'short',
    eyes: 'normal',
    clothing: 'hoodie'
  },
  animations: {
    idle: 'wave',
    celebration: 'cheer',
    thinking: 'scratch-head'
  }
};

// Generate deterministic avatar config from username
export function generateAvatarFromUsername(username: string): AvatarConfig {
  const hash = username.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const categories = Object.keys(AVATAR_CATEGORIES) as (keyof typeof AVATAR_CATEGORIES)[];
  const colors = Object.values(AVATAR_COLORS.teen);
  
  const category = categories[hash % categories.length];
  const styles = AVATAR_CATEGORIES[category].styles;
  const style = styles[hash % styles.length];
  
  return {
    category,
    style,
    colors: {
      primary: colors[hash % colors.length],
      secondary: colors[(hash + 1) % colors.length],
      accent: colors[(hash + 2) % colors.length]
    },
    features: {
      hair: 'random',
      eyes: 'normal',
      clothing: 'default'
    },
    animations: {
      idle: AVATAR_ANIMATIONS.idle[hash % AVATAR_ANIMATIONS.idle.length],
      celebration: AVATAR_ANIMATIONS.celebration[hash % AVATAR_ANIMATIONS.celebration.length],
      thinking: AVATAR_ANIMATIONS.thinking[hash % AVATAR_ANIMATIONS.thinking.length]
    },
    seed: username
  };
}